import { Space } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useState } from "react";
import {
    bindPendingChatContext,
    clearPendingChatContext,
    type PendingChatContext,
    peekPendingChatContext,
    writePendingChatContext,
} from "../components/MetadataDocuments/services/pendingChatContext";
import { db } from "../db";
import { useChatId } from "../hooks/useChatId";
import { ChatRouteChild } from "./ChatRoute";

interface ChatRouteProps {
    hideHeader?: boolean;
    isCompact?: boolean;
}

export function ChatRoute({ hideHeader, isCompact }: ChatRouteProps = {}) {
    const navigate = useNavigate();
    const chatId = useChatId();
    const [pendingContext, setPendingContext] = useState<PendingChatContext | undefined>(() =>
        peekPendingChatContext(),
    );

    useEffect(() => {
        if (window.location.hash === "#/") {
            navigate({ to: "/" });
        }
    }, [navigate]);

    useEffect(() => {
        if (chatId && pendingContext && !pendingContext.boundChatId) {
            bindPendingChatContext(chatId.toString());
            setPendingContext(peekPendingChatContext());
        }
    }, [chatId, pendingContext]);

    const clearDocumentsViewerContext = useCallback(() => {
        clearPendingChatContext();
        setPendingContext(undefined);
    }, []);

    const handleClearContext = useCallback(
        (label?: string) => {
            if (!pendingContext) return;

            // No label (or clearing the fixed datasource) removes the whole
            // documents-viewer filter set so user preferences show again.
            if (!label) {
                clearDocumentsViewerContext();
                return;
            }

            // Tags carry the column label, filters the attribute name; both are built
            // from the same active-filter list, so they share an index.
            const removed = pendingContext.contextTags.findIndex((tag) => tag.label === label);
            const nextTags = pendingContext.contextTags.filter((_, i) => i !== removed);
            const nextMetadata = pendingContext.metadataFilter.filter((_, i) => i !== removed);

            if (nextTags.length === 0) {
                clearDocumentsViewerContext();
                return;
            }

            const nextContext: PendingChatContext = {
                ...pendingContext,
                contextTags: nextTags,
                metadataFilter: nextMetadata,
            };
            writePendingChatContext(nextContext);
            setPendingContext(nextContext);
        },
        [pendingContext, clearDocumentsViewerContext],
    );

    const contextChatId = pendingContext?.boundChatId;
    const contextApplies = Boolean(
        pendingContext && (!contextChatId || contextChatId === chatId?.toString()),
    );
    const activeContext = contextApplies ? pendingContext : undefined;

    const messages = useLiveQuery(() => {
        if (!chatId) return [];
        return db.messages.where("chatId").equals(chatId).sortBy("createdAt");
    }, [chatId]);

    return (
        <>
            {!isCompact && <Space h="lg" />}
            <ChatRouteChild
                chatId={chatId ?? undefined}
                messages={messages}
                hideHeader={hideHeader}
                isCompact={isCompact}
                contextTags={activeContext?.contextTags}
                metadataFilter={activeContext?.metadataFilter}
                fixedDatasources={activeContext?.datasources}
                fixedDatasourceLabel={activeContext?.datasourceLabel}
                onClearContext={activeContext ? handleClearContext : undefined}
            />
        </>
    );
}
